"use client";
import { useState } from "react";

const features = [
  {
    title: "Mobile Control",
    description: "Control your motors through App, SMS, or IVRS with real-time status updates and power alerts.",
    details: "The Kisan Mithraa mobile starters work just like mobile control for your motor. Farmers can easily start or stop the motor from anywhere, check power status in real time, and get instant alerts on faults or power cuts. With automatic scheduling, dry run and overload protection, it ensures safety, saves energy, and makes farming simple and smart.",
    video: "https://www.youtube.com/embed/juZ_daqwq1U?si=xmhrxp8FqeDtG1fw",
  },
  {
    title: "Easy Installation", 
    description: "Quick setup with minimal technical expertise. Includes user manual and customer support.",
    details: [
      "Remove the existing preventer from your starter box.",
      "Fit the Kisan Mithraa starter inside your main starter box.",
      "Connect wires (with our video call support): R, Y, B → Phase wires; Two black wires → Terminals 93 & 94.",
      "Remove link wire from 93 (for single relay).",
      "For double relay → connect 2 green wires, one from 96 to magnet coil.",
      "Insert SIM card (Airtel, Idea, BSNL – Jio not supported).",
      "Insert fuses into your starter.",
      "Check lights: Red (3-Phase) & Green (Signal) should blink.",
      "Call customer care – they will link your starter to 3 mobile numbers."
    ],
    video: "https://www.youtube.com/embed/C5Al9zzht-s",
  },
  {
    title: "Advanced Features",
    description: "Weather-resistant design with energy-efficient components and automatic safety shutdown.",
    details: "The Kisan Mithraa mobile starters bring smart control and safety to farming. With mobile app access, farmers can start or stop motors from anywhere, monitor power and performance in real time, and get instant alerts during faults or power cuts. Features like dry run and overload protection, automatic scheduling, and multi-language support make them reliable, energy-saving, and easy to use—perfect for modern agriculture",
    video: "https://www.youtube.com/embed/juZ_daqwq1U?si=xmhrxp8FqeDtG1fw",
  },
  {
    title: "Built to Last",
    description: "Premium materials and rigorous quality checks ensure years of reliable performance.",
    details: "Regular software updates, maintenance alerts, and extended lifecycle with continuous operation support.",
    video: "https://www.youtube.com/embed/juZ_daqwq1U?si=xmhrxp8FqeDtG1fw",
  },
];

export default function ProductFeatures() {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <section  className="py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div  className="text-center mb-16">
          <h2 className="header-title text-5xl font-bold text-black mb-4">
            Key Benefits of  <span className="text-[#6BAF92]">
                Kisan Mithraa Mobile Starter
            </span>
          </h2>
          <p className="header-subtitle text-gray-600 max-w-3xl mx-auto text-2xl">
            Smart farming solutions designed for modern agriculture
          </p>
        </div>

        {/* Features Grid */}
        <div  className="grid gap-12">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`feature-item grid lg:grid-cols-2 gap-8 items-center ${
                index % 2 === 1 ? "lg:grid-flow-col-dense" : ""
              }`}
            >
              {/* Content */}
              <div className={`feature-content ${index % 2 === 1 ? "lg:col-start-2" : ""}`}>
                <h3 className="text-2xl font-semibold text-[#6BAF92] mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {feature.description}
                </p>
                
                {expanded === index && (
                  <div className="text-gray-600 mb-4 leading-relaxed animate-fade-in">
                    {Array.isArray(feature.details) ? (
                      <ul className="list-disc list-inside space-y-2">
                        {feature.details.map((detail, i) => (
                          <li key={i}>{detail}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>{feature.details}</p>
                    )}
                  </div>
                )}

                <button
                  onClick={() => setExpanded(expanded === index ? null : index)}
                  className="inline-flex items-center text-gray-600 font-medium hover:text-gray-700 transition-all duration-300 hover:scale-105 cursor-pointer"
                >
                  {expanded === index ? "Show Less" : "Learn More"}
                  <svg 
                    className={`ml-2 w-4 h-4 transition-transform duration-300 ${
                      expanded === index ? "rotate-180" : ""
                    }`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Video */}
              <div className={`feature-video ${index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}`}>
                <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-gray-200 hover:shadow-xl transition-shadow duration-300">
                  <iframe
                    src={feature.video}
                    title={feature.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom CSS for fade-in animation */}
      <style jsx>{`
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}